import {
  DEFAULT_ENTRY_FILENAME,
  DEFAULT_VERSION_PATTERN,
  LOCAL_HOSTNAMES,
  PORT_RANGE,
} from './constants';

export function getLocalhostUrl(port: number, filename = DEFAULT_ENTRY_FILENAME): string {
  if (!Number.isInteger(port) || port < PORT_RANGE.min || port > PORT_RANGE.max) {
    throw new RangeError(
      `Invalid port number: ${port}. Must be an integer between ${PORT_RANGE.min} and ${PORT_RANGE.max}.`,
    );
  }
  return `http://localhost:${port}/${filename}`;
}

export function getStagingUrl(
  cdnBase: string,
  cdnPath: string,
  version: string,
  filename = DEFAULT_ENTRY_FILENAME,
): string {
  const base = cdnBase.replace(/\/$/, '');
  const path = cdnPath.replace(/^\/|\/$/g, '');
  return `${base}/${path}/${version}/${filename}`;
}

export interface ParsedEntryUrl {
  cdnBase: string;
  cdnPath: string;
  version: string;
  filename: string;
}

export function parseEntryUrl(url: string): ParsedEntryUrl | null {
  try {
    const parsed = new URL(url);

    if (LOCAL_HOSTNAMES.has(parsed.hostname)) {
      return {
        cdnBase: `${parsed.protocol}//${parsed.host}`,
        cdnPath: '',
        version: 'local',
        filename: parsed.pathname.replace(/^\//, ''),
      };
    }

    const pathParts = parsed.pathname.split('/').filter(Boolean);
    if (pathParts.length < 2) return null;

    const filename = pathParts.pop()!;
    const version = pathParts.pop()!;
    const cdnPath = pathParts.join('/');

    if (!DEFAULT_VERSION_PATTERN.test(version)) return null;

    return {
      cdnBase: `${parsed.protocol}//${parsed.host}`,
      cdnPath,
      version,
      filename,
    };
  } catch {
    return null;
  }
}

export function isLocalhostUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1';
  } catch {
    return false;
  }
}

export function getLocalhostPort(url: string): number | null {
  try {
    const parsed = new URL(url);
    if (parsed.hostname !== 'localhost' && parsed.hostname !== '127.0.0.1') {
      return null;
    }
    if (!parsed.port) return null;
    const port = parseInt(parsed.port, 10);
    return Number.isNaN(port) ? null : port;
  } catch {
    return null;
  }
}

/**
 * Validate that a URL points to a local development server.
 * Used to gate dynamic script loading — only localhost URLs are trusted
 * for hot-reload and override injection.
 */
export function isValidDevEntryUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return false;
    if (parsed.hostname !== 'localhost' && parsed.hostname !== '127.0.0.1') return false;
    return true;
  } catch {
    return false;
  }
}

/**
 * Validate that a URL is safe to load as a federated remoteEntry.js script.
 * Accepts https from ANY origin or http://localhost (dev convenience).
 * Rejects javascript:, data:, file:, plus any non-localhost http origin.
 *
 * NOT a mirror of the shell's `validateRemoteUrl` — the contracts diverge by
 * design:
 *   - Shell loads tenant-bound bundles, so `validateRemoteUrl` restricts to
 *     `*.abax.cloud` / `*.track2find.com` to defend against tampered
 *     federation-config redirecting at attacker hosts (CWE-829).
 *   - This function gates the platform parcel-loader, which serves dev/test
 *     parcels from arbitrary https origins (designer prototypes, vendor
 *     CDNs, ephemeral review environments) — a tenant allowlist would block
 *     legitimate parcel workflows.
 *
 * If you need shell-grade tenant pinning, add a separate `isValidTenantUrl`
 * with the explicit allowlist; do not tighten this function.
 */
export function isValidEntryUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    if (parsed.protocol === 'https:') return true;
    if (parsed.protocol === 'http:') {
      return parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1';
    }
    return false;
  } catch {
    return false;
  }
}
